import { useCallback, useEffect, useMemo } from 'react';

import size from '@/assets/theme/size';
import useStore from '@/state';
import closeNavigationAction from '@/state/selectors/navigation/close-navigation';
import getDropdownSegments from '@/state/selectors/navigation/get-dropdown-segments';
import getOpenState from '@/state/selectors/navigation/get-open-state';
import isBreadcrumbOpenSelector from '@/state/selectors/secondary-nav/is-breadcrumb-open';
import isCurrentWidthWiderThan from '@/state/selectors/window/is-current-width-wider-than';
import { IStore } from '@/state/slices/typings';
import EntryPointsHelper from '@/utils/entry-points-helper';

const { laptop } = size;

const usePortals = () => {
	const closeNavigation = useStore(closeNavigationAction);

	const open = useStore(getOpenState);
	const isBreadcrumbOpen = useStore(isBreadcrumbOpenSelector);
	const dropdownSegments = useStore(getDropdownSegments);
	const isWiderThanLaptop = useStore((state: IStore) =>
		isCurrentWidthWiderThan(state, laptop),
	);

	const portalEntryPoint = useMemo(
		() => EntryPointsHelper.portalEntryPoint,
		[],
	);

	const onEscClick = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') closeNavigation();
		},
		[closeNavigation],
	);

	const hidePortal = useMemo(
		() =>
			dropdownSegments.length === 1 &&
			dropdownSegments[0] === 'mobileRoot' &&
			isWiderThanLaptop,
		[dropdownSegments, isWiderThanLaptop],
	);

	const cleanDocumentElementStyle = useCallback(() => {
		if (document.documentElement.getAttribute('style') === '') {
			document.documentElement.removeAttribute('style');
		}
	}, []);

	const lock = useCallback(() => {
		globalThis.addEventListener('keydown', onEscClick);
		document.documentElement.style.overflow = 'hidden';
		document.documentElement.style.overflowY = 'hidden';
	}, [onEscClick]);

	const unlock = useCallback(() => {
		globalThis.removeEventListener('keydown', onEscClick);
		(document.documentElement.style.overflow as any) = null;
		(document.documentElement.style.overflowY as any) = null;
		cleanDocumentElementStyle();
	}, [onEscClick, cleanDocumentElementStyle]);

	useEffect(() => {
		const lockAction =
			(open || isBreadcrumbOpen) && !hidePortal ? lock : unlock;
		lockAction();

		return unlock;
	}, [lock, unlock, hidePortal, open, isBreadcrumbOpen]);

	return {
		portalEntryPoint,
		hidePortal,
	};
};

export default usePortals;
